import React, { useMemo, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';

// ── Period extraction ──────────────────────────────────────────────────────
// Matches Q1_2025, H1_2025, FY2025, FY_2025 anywhere in a field name
const PERIOD_RE = /(Q[1-4]_\d{4}|H[1-2]_\d{4}|FY_?\d{4})/i;

function extractPeriodInfo(fieldName) {
  const upper = fieldName.toUpperCase();
  const m = upper.match(PERIOD_RE);
  if (!m) return null;
  const period = m[1].replace(/FY(\d)/, 'FY_$1'); // normalize FY2025 → FY_2025
  const base = upper
    .replace(m[1], '')
    .replace(/__+/g, '_')
    .replace(/^_+|_+$/g, '');
  return { period, base };
}

function sortKeyForPeriod(p) {
  const q = p.match(/^Q([1-4])_(\d{4})$/);
  if (q) return parseInt(q[2]) * 10 + parseInt(q[1]);
  const h = p.match(/^H([12])_(\d{4})$/);
  if (h) return parseInt(h[2]) * 10 - 2 + parseInt(h[1]);
  const fy = p.match(/^FY_?(\d{4})$/);
  if (fy) return parseInt(fy[1]) * 10 - 5;
  return 0;
}

function formatPeriodLabel(period) {
  const q = period.match(/^Q([1-4])_(\d{4})$/);
  if (q) return `Q${q[1]} '${q[2].slice(2)}`;
  const h = period.match(/^H([12])_(\d{4})$/);
  if (h) return `H${h[1]} '${h[2].slice(2)}`;
  const fy = period.match(/^FY_?(\d{4})$/);
  if (fy) return `FY '${fy[1].slice(2)}`;
  return period;
}

// ── Value parsing ──────────────────────────────────────────────────────────
function parseValue(val) {
  if (val === null || val === undefined || val === '') return null;
  const s = String(val).replace(/[$,\s~≈]/g, '').replace(/^[><]/g, '');
  if (/^-?[\d.]+[Bb]$/.test(s)) return parseFloat(s) * 1000;
  if (/^-?[\d.]+[Mm]$/.test(s)) return parseFloat(s);
  if (/^-?[\d.]+[Kk]$/.test(s)) return parseFloat(s) / 1000;
  const n = parseFloat(s.replace(/%$/, '').replace(/[xX]$/, ''));
  return isNaN(n) ? null : n;
}

// ── Flatten all sections of a record into {key: value} ───────────────────
function flattenRecordFields(fields) {
  const flat = {};
  for (const [sectionKey, sectionVal] of Object.entries(fields || {})) {
    if (sectionKey === '_meta') continue;
    if (sectionKey === '_root') {
      if (sectionVal && typeof sectionVal === 'object') Object.assign(flat, sectionVal);
      continue;
    }
    if (sectionVal && typeof sectionVal === 'object' && !Array.isArray(sectionVal)) {
      Object.assign(flat, sectionVal);
    } else {
      flat[sectionKey] = sectionVal;
    }
  }
  return flat;
}

// ── Display label utilities ──────────────────────────────────────────────
// Strips trailing scale/accounting qualifiers so each variant gets a
// distinct readable name:
//   ADJUSTED_NET_INCOME_MILLIONS → "Adj. Net Income ($M)"
//   ADJUSTED_NET_INCOME_NON_GAAP → "Adj. Net Income (Non-GAAP)"
//   ADJUSTED_NET_INCOME          → "Adj. Net Income"
const DISPLAY_SUFFIXES = [
  [/_IN_MILLIONS$/, '($M)'],
  [/_MILLIONS$/, '($M)'],
  [/_IN_THOUSANDS$/, '($K)'],
  [/_THOUSANDS$/, '($K)'],
  [/_IN_BILLIONS$/, '($B)'],
  [/_BILLIONS$/, '($B)'],
  [/_NON_GAAP$/, '(Non-GAAP)'],
  [/_GAAP$/, '(GAAP)'],
  [/_PER_SHARE_DILUTED$/, '/ Share (Dil.)'],
  [/_PER_SHARE_BASIC$/, '/ Share (Basic)'],
  [/_PER_SHARE$/, '/ Share'],
  [/_DILUTED$/, '(Dil.)'],
  [/_BASIC$/, '(Basic)'],
];

const LABEL_SUBS = [
  [/^PLATFORM_AND_REFERRAL_FEES_NET$/, 'Platform/Referral Fees'],
  [/^REVENUE_FROM_FEES_NET$/, 'Revenue from Fees'],
  [/^TOTAL_REVENUE$/, 'Total Revenue'],
  [/^TOTAL_REVENUE_YOY_GROWTH$/, 'Total Revenue YoY %'],
  [/^NET_REVENUE$/, 'Net Revenue'],
  [/^NET_REVENUE_YOY_GROWTH$/, 'Net Revenue YoY %'],
  [/^REVENUE_YOY_GROWTH$/, 'Revenue YoY %'],
  [/^ADJUSTED_EBITDA_MARGIN$/, 'Adj. EBITDA Margin'],
  [/^ADJUSTED_EBITDA$/, 'Adj. EBITDA'],
  [/^CONTRIBUTION_PROFIT_MARGIN$/, 'Contribution Profit Margin'],
  [/^CONTRIBUTION_PROFIT$/, 'Contribution Profit'],
  [/^CONTRIBUTION_MARGIN$/, 'Contribution Margin'],
  [/^ADJUSTED_NET_INCOME_LOSS$/, 'Adj. Net Income/Loss'],
  [/^ADJUSTED_NET_INCOME$/, 'Adj. Net Income'],
  [/^ADJUSTED_NET_LOSS$/, 'Adj. Net Loss'],
  [/^NET_INCOME_LOSS$/, 'Net Income/Loss'],
  [/^NET_INCOME$/, 'Net Income'],
  [/^NET_LOSS$/, 'Net Loss'],
  [/^OPERATING_INCOME_LOSS$/, 'Operating Income/Loss'],
  [/^OPERATING_INCOME$/, 'Operating Income'],
  [/^PERSONAL_LOAN_ORIGINATIONS$/, 'PL Originations'],
  [/^AUTO_ORIGINATIONS$/, 'Auto Originations'],
  [/^HOME_ORIGINATIONS$/, 'Home Originations'],
  [/^AUTO_NUMBER_OF_LOANS$/, 'Auto Loan Count'],
  [/^PERSONAL_LOAN_NUMBER_OF_LOANS$/, 'PL Loan Count'],
  [/^PERSONAL_LOAN_CORE_ORIGINATIONS$/, 'PL Core Originations'],
  [/^PERSONAL_LOAN_SUPER_PRIME_ORIGINATIONS$/, 'PL Super-Prime Orig.'],
  [/^SALES_AND_MARKETING$/, 'Sales & Marketing'],
  [/^GENERAL_AND_ADMINISTRATIVE$/, 'G&A'],
  [/^RESEARCH_AND_DEVELOPMENT$/, 'R&D'],
  [/^OPERATING_EXPENSES$/, 'Operating Expenses'],
  [/^CONVERSION_RATE$/, 'Conversion Rate'],
];

function shortLabel(base) {
  // Strip trailing qualifiers; remember them to append to the display name
  let core = base;
  const qualifiers = [];
  let changed = true;
  while (changed) {
    changed = false;
    for (const [pattern, label] of DISPLAY_SUFFIXES) {
      if (pattern.test(core)) {
        core = core.replace(pattern, '');
        qualifiers.unshift(label);
        changed = true;
        break;
      }
    }
  }
  // Match the stripped core against human-readable names
  let mainLabel = null;
  for (const [pattern, label] of LABEL_SUBS) {
    if (pattern.test(core)) { mainLabel = label; break; }
  }
  // Fallback: title-case the core key
  if (!mainLabel) {
    mainLabel = core
      .replace(/_PCT$/, ' %')
      .replace(/_PERCENT$/, ' %')
      .replace(/_NET$/, '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return qualifiers.length ? `${mainLabel} ${qualifiers.join(' ')}` : mainLabel;
}

// ── Chart group definitions ───────────────────────────────────────────────
const CHART_COLORS = [
  '#c9a84c', '#6dbb88', '#88aaee', '#dd9944', '#cc7777',
  '#88ccaa', '#ddbb66', '#aa88cc', '#66bbdd', '#dd8866',
];

const CHART_DEFS = [
  {
    id: 'revenue',
    title: 'Revenue',
    icon: 'bi-cash-stack',
    unit: '$M',
    type: 'bar',
    include: (base) =>
      (base.includes('REVENUE') || base.includes('FEES_NET') || base.includes('TOTAL_REVENUE')) &&
      !base.includes('GUIDANCE') && !base.includes('YOY') && !base.includes('QOQ') &&
      !base.includes('GROWTH') && !base.includes('FAIR_VALUE') && !base.includes('EXPENSE'),
  },
  {
    id: 'revenue_growth',
    title: 'YoY Revenue Growth',
    icon: 'bi-graph-up-arrow',
    unit: '%',
    type: 'line',
    include: (base) => base.endsWith('_YOY_GROWTH'),
  },
  {
    id: 'net_income',
    title: 'Net Income',
    icon: 'bi-wallet2',
    unit: '$M',
    type: 'bar',
    include: (base) =>
      (base.includes('NET_INCOME') || base.includes('NET_LOSS') ||
       base.includes('ADJUSTED_NET_INCOME') || base.includes('ADJUSTED_NET_LOSS')) &&
      !base.includes('MARGIN') && !base.includes('PCT') && !base.includes('GUIDANCE') &&
      !base.includes('PER_SHARE') && !base.includes('YOY') && !base.includes('GROWTH'),
  },
  {
    id: 'originations_product',
    title: 'Loan Originations by Product',
    icon: 'bi-bank',
    unit: '$M',
    type: 'bar',
    include: (base) =>
      base.includes('ORIGINATIONS') &&
      !base.includes('NUMBER') && !base.includes('COUNT') &&
      !base.includes('YOY') && !base.includes('QOQ') && !base.includes('GROWTH') &&
      !base.includes('GUIDANCE') && !base.includes('PCT'),
  },
];

// ── Derive a quarter period string from an ISO date string ───────────────
function periodFromDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return `Q${q}_${d.getFullYear()}`;
}

// ── Build series map from records ─────────────────────────────────────────
function buildSeriesMap(records) {
  // Process each record individually so we can fall back to its period_end_date
  // for records where field names don't embed a period token (new flat format).
  const seriesMap = {};
  for (const rec of [...records].reverse()) {
    const flat = flattenRecordFields(rec.fields);
    const fallbackPeriod = periodFromDate(rec.period_end_date);
    for (const [fieldName, rawVal] of Object.entries(flat)) {
      const info = extractPeriodInfo(fieldName);
      let base, period;
      if (info) {
        base = info.base;
        period = info.period;
      } else if (fallbackPeriod) {
        base = fieldName.toUpperCase();
        period = fallbackPeriod;
      } else {
        continue;
      }
      const num = parseValue(rawVal);
      if (num === null) continue;
      if (!seriesMap[base]) seriesMap[base] = {};
      seriesMap[base][period] = num;
    }
  }
  const mergedMap = mergeCompatibleSeries(seriesMap);
  addYoYGrowthSeries(mergedMap);
  return mergedMap;
}

// ── Compute YoY growth rates ──────────────────────────────────────────────
// For each revenue-related series, compute quarter-over-same-quarter growth
// and add a synthetic `<BASE>_YOY_GROWTH` series (values in %).
function addYoYGrowthSeries(seriesMap) {
  const revenueBases = Object.keys(seriesMap).filter(
    (b) =>
      (b.includes('REVENUE') || b.includes('FEES_NET') || b.includes('TOTAL_REVENUE')) &&
      !b.includes('GUIDANCE') && !b.includes('YOY') && !b.includes('GROWTH') &&
      !b.includes('FAIR_VALUE') && !b.includes('EXPENSE'),
  );
  for (const base of revenueBases) {
    const periods = seriesMap[base];
    const growthKey = `${base}_YOY_GROWTH`;
    const growth = {};
    for (const [period, value] of Object.entries(periods)) {
      const qm = period.match(/^Q([1-4])_(\d{4})$/);
      if (!qm) continue;
      const priorPeriod = `Q${qm[1]}_${parseInt(qm[2]) - 1}`;
      const priorVal = periods[priorPeriod];
      if (priorVal !== undefined && priorVal !== 0) {
        growth[period] = parseFloat((((value - priorVal) / Math.abs(priorVal)) * 100).toFixed(2));
      }
    }
    if (Object.keys(growth).length > 0) {
      seriesMap[growthKey] = growth;
    }
  }
}

// ── Unit-suffix merge ──────────────────────────────────────────────────────
// After building the raw seriesMap, look for bases whose names differ only
// by a trailing unit/scale suffix (e.g. FOO_MILLIONS vs FOO). If their
// values for shared time periods are the same order of magnitude (×10 max
// ratio) they are the same metric labelled differently across documents and
// can safely be merged. If values diverge by >10× on any shared period the
// bases are left separate (genuine unit conflict).
function stripUnitSuffix(base) {
  return base.replace(
    /_(IN_MILLIONS|IN_THOUSANDS|IN_BILLIONS|MILLIONS|THOUSANDS|BILLIONS|MM|USD)$/,
    ''
  );
}

function mergeCompatibleSeries(seriesMap) {
  // Group bases by their unit-stripped name
  const groups = {};
  for (const base of Object.keys(seriesMap)) {
    const stripped = stripUnitSuffix(base);
    if (!groups[stripped]) groups[stripped] = [];
    groups[stripped].push(base);
  }

  const result = { ...seriesMap };

  for (const [canonical, bases] of Object.entries(groups)) {
    if (bases.length < 2) continue;

    // Safety check: are values consistent on shared periods?
    const allPeriods = [...new Set(bases.flatMap((b) => Object.keys(seriesMap[b])))]; 
    let hasConflict = false;
    for (const period of allPeriods) {
      const vals = bases
        .map((b) => seriesMap[b][period])
        .filter((v) => v !== undefined)
        .map(Math.abs)
        .filter((v) => v > 0);
      if (vals.length < 2) continue;
      const ratio = Math.max(...vals) / Math.min(...vals);
      if (ratio > 10) { hasConflict = true; break; }
    }
    if (hasConflict) continue;

    // Merge all period data; canonical (no suffix) key's values win on ties
    const merged = {};
    for (const base of bases) Object.assign(merged, seriesMap[base]);
    if (seriesMap[canonical]) Object.assign(merged, seriesMap[canonical]);

    for (const base of bases) delete result[base];
    result[canonical] = merged;
  }

  return result;
}

function buildGroupData(chartDef, seriesMap) {
  const matchingBases = Object.keys(seriesMap).filter(chartDef.include);
  if (matchingBases.length === 0) return null;

  // Sort by number of periods desc, take top 6
  const top = matchingBases
    .sort((a, b) => Object.keys(seriesMap[b]).length - Object.keys(seriesMap[a]).length)
    .slice(0, 6);

  // Collect all periods across matching bases, sorted chronologically.
  // Exclude half-year (H1/H2) and full-year (FY) periods — quarterly only.
  const periods = [...new Set(top.flatMap((b) => Object.keys(seriesMap[b])))]
    .filter((p) => /^Q[1-4]_\d{4}$/i.test(p))
    .sort((a, b) => sortKeyForPeriod(a) - sortKeyForPeriod(b));

  if (periods.length === 0) return null;

  // Drop bases that have no data in the quarterly-filtered periods
  const activeBases = top.filter((b) =>
    periods.some((p) => seriesMap[b][p] !== undefined)
  );
  if (activeBases.length === 0) return null;

  const data = periods.map((period) => {
    const row = { period, periodLabel: formatPeriodLabel(period) };
    for (const base of activeBases) {
      if (seriesMap[base][period] !== undefined) {
        row[base] = seriesMap[base][period];
      }
    }
    return row;
  });

  return { data, bases: activeBases };
}

// ── Custom tooltip ────────────────────────────────────────────────────────
function EarningsTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  const validItems = payload.filter((p) => p.value !== undefined && p.value !== null);
  if (!validItems.length) return null;
  return (
    <div style={{
      backgroundColor: '#111008',
      border: '1px solid #3a2e00',
      padding: '8px 12px',
      borderRadius: 6,
      fontSize: '0.75rem',
      maxWidth: 260,
    }}>
      <div style={{ color: '#f0c040', fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {validItems.map((p, i) => (
        <div key={i} style={{ color: p.color, marginBottom: 2 }}>
          <span style={{ color: '#9a8a60' }}>{shortLabel(p.dataKey)}: </span>
          <span style={{ fontWeight: 600 }}>
            {typeof p.value === 'number'
              ? p.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : p.value}
            {unit === '%' ? '%' : unit === '$M' ? 'M' : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Single chart card ─────────────────────────────────────────────────────
function ChartBody({ def, data, bases, dark, chartHeight, hiddenBases, onToggleBase }) {
  const yFormatter = (v) => {
    if (typeof v !== 'number') return v;
    if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}B`;
    if (Math.abs(v) >= 100) return v.toFixed(0);
    if (Math.abs(v) >= 10) return v.toFixed(1);
    return v.toFixed(2);
  };

  const visibleBases = bases.filter((b) => !hiddenBases.has(b));

  const hasNegative = data.some((row) =>
    visibleBases.some((b) => typeof row[b] === 'number' && row[b] < 0)
  );

  const fontSize = chartHeight > 300 ? 12 : 10;

  return (
    <>
      <ResponsiveContainer width="100%" height={chartHeight}>
        {def.type === 'line' ? (
          <LineChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2000" />
            <XAxis
              dataKey="periodLabel"
              tick={{ fill: dark.muted, fontSize }}
              tickLine={false}
              axisLine={{ stroke: '#3a2e00' }}
            />
            <YAxis
              tickFormatter={yFormatter}
              tick={{ fill: dark.muted, fontSize }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={(props) => <EarningsTooltip {...props} unit={def.unit} />} />
            {hasNegative && <ReferenceLine y={0} stroke="#5a4a00" strokeDasharray="4 2" />}
            {visibleBases.map((base) => {
              const i = bases.indexOf(base);
              return (
                <Line
                  key={base}
                  type="monotone"
                  dataKey={base}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  strokeWidth={2}
                  dot={{ fill: CHART_COLORS[i % CHART_COLORS.length], r: chartHeight > 300 ? 4 : 3 }}
                  activeDot={{ r: chartHeight > 300 ? 6 : 5 }}
                  connectNulls
                />
              );
            })}
          </LineChart>
        ) : (
          <BarChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2000" vertical={false} />
            <XAxis
              dataKey="periodLabel"
              tick={{ fill: dark.muted, fontSize }}
              tickLine={false}
              axisLine={{ stroke: '#3a2e00' }}
            />
            <YAxis
              tickFormatter={yFormatter}
              tick={{ fill: dark.muted, fontSize }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={(props) => <EarningsTooltip {...props} unit={def.unit} />} />
            {hasNegative && <ReferenceLine y={0} stroke="#5a4a00" strokeDasharray="4 2" />}
            {visibleBases.map((base) => {
              const i = bases.indexOf(base);
              return (
                <Bar
                  key={base}
                  dataKey={base}
                  fill={CHART_COLORS[i % CHART_COLORS.length]}
                  radius={visibleBases.length === 1 ? [3, 3, 0, 0] : [2, 2, 0, 0]}
                  maxBarSize={chartHeight > 300 ? 64 : 48}
                />
              );
            })}
          </BarChart>
        )}
      </ResponsiveContainer>

      {/* Clickable legend */}
      {bases.length > 1 && (
        <div
          className="d-flex flex-wrap gap-2 px-3 mt-1"
          style={{ fontSize: chartHeight > 300 ? '0.72rem' : '0.62rem' }}
        >
          {bases.map((base, i) => {
            const hidden = hiddenBases.has(base);
            return (
              <span
                key={base}
                onClick={() => onToggleBase(base)}
                style={{
                  color: '#9a8a60',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'pointer',
                  opacity: hidden ? 0.35 : 1,
                  textDecoration: hidden ? 'line-through' : 'none',
                  userSelect: 'none',
                }}
                title={hidden ? `Show ${shortLabel(base)}` : `Hide ${shortLabel(base)}`}
              >
                <span style={{
                  width: def.type === 'line' ? 16 : 10,
                  height: def.type === 'line' ? 2 : 10,
                  borderRadius: def.type === 'line' ? 0 : 2,
                  backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                  flexShrink: 0,
                  display: 'inline-block',
                }} />
                <span style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}>
                  {shortLabel(base)}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </>
  );
}

function SingleChart({ def, data, bases, dark }) {
  const [collapsed, setCollapsed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [hiddenBases, setHiddenBases] = useState(new Set());

  const toggleBase = (base) => {
    setHiddenBases((prev) => {
      const next = new Set(prev);
      if (next.has(base)) next.delete(base);
      else next.add(base);
      return next;
    });
  };

  const headerBtn = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setFullscreen(true); }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: dark.muted,
          fontSize: '0.7rem',
          padding: '0 4px',
        }}
        title="Fullscreen"
      >
        <i className="bi bi-arrows-fullscreen" />
      </button>
      <i className={`bi bi-chevron-${collapsed ? 'right' : 'down'}`} style={{ fontSize: '0.65rem', color: dark.muted }} />
    </div>
  );

  return (
    <>
      <div style={{
        backgroundColor: dark.card,
        border: `1px solid ${dark.border}`,
        borderRadius: 8,
        overflow: 'hidden',
      }}>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '7px 12px',
            background: '#0f0c02',
            border: 'none',
            borderBottom: collapsed ? 'none' : `1px solid ${dark.border}`,
            cursor: 'pointer',
            color: dark.goldBright,
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            textAlign: 'left',
          }}
        >
          <span>
            <i className={`bi ${def.icon} me-2`} />
            {def.title}
            <span style={{ color: dark.muted, fontWeight: 400, fontSize: '0.65rem', marginLeft: 6 }}>
              ({def.unit})
            </span>
          </span>
          {headerBtn}
        </button>

        {!collapsed && (
          <div style={{ padding: '12px 4px 8px 0' }}>
            <ChartBody def={def} data={data} bases={bases} dark={dark} chartHeight={200} hiddenBases={hiddenBases} onToggleBase={toggleBase} />
          </div>
        )}
      </div>

      {/* Fullscreen overlay */}
      {fullscreen && (
        <div
          onClick={() => setFullscreen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: dark.card,
              border: `1px solid ${dark.border}`,
              borderRadius: 10,
              width: '92vw',
              maxWidth: 1200,
              padding: '18px 24px 24px',
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 14,
            }}>
              <span style={{
                color: dark.goldBright,
                fontWeight: 700,
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}>
                <i className={`bi ${def.icon} me-2`} />
                {def.title}
                <span style={{ color: dark.muted, fontWeight: 400, fontSize: '0.72rem', marginLeft: 8 }}>
                  ({def.unit})
                </span>
              </span>
              <button
                onClick={() => setFullscreen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: dark.muted,
                  fontSize: '1rem',
                }}
                title="Close"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <ChartBody def={def} data={data} bases={bases} dark={dark} chartHeight={Math.min(window.innerHeight * 0.65, 500)} hiddenBases={hiddenBases} onToggleBase={toggleBase} />
          </div>
        </div>
      )}
    </>
  );
}

// ── Main export ───────────────────────────────────────────────────────────
export default function EarningsCharts({ records }) {
  const [sectionCollapsed, setSectionCollapsed] = useState(false);

  const seriesMap = useMemo(() => {
    if (!records || records.length === 0) return {};
    return buildSeriesMap(records);
  }, [records]);

  const chartGroups = useMemo(() =>
    CHART_DEFS
      .map((def) => ({ def, result: buildGroupData(def, seriesMap) }))
      .filter(({ result }) => result !== null && result.data.length > 0),
    [seriesMap]
  );

  if (chartGroups.length === 0) return null;

  const dark = {
    bg: '#0a0900',
    card: '#111008',
    border: '#3a2e00',
    gold: '#c9a84c',
    goldBright: '#f0c040',
    muted: '#7a6a40',
    mutedLight: '#9a8a60',
    text: '#fff',
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Section header */}
      <button
        type="button"
        onClick={() => setSectionCollapsed((c) => !c)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: '#0c0b02',
          border: `1px solid ${dark.border}`,
          borderRadius: sectionCollapsed ? 7 : '7px 7px 0 0',
          padding: '8px 14px',
          cursor: 'pointer',
          color: dark.goldBright,
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          textAlign: 'left',
        }}
      >
        <i className={`bi bi-chevron-${sectionCollapsed ? 'right' : 'down'}`} style={{ fontSize: '0.7rem', color: dark.muted }} />
        <i className="bi bi-bar-chart-fill" />
        Earnings Charts
        <span style={{ color: dark.muted, fontWeight: 400, fontSize: '0.68rem', marginLeft: 4 }}>
          — {chartGroups.length} chart{chartGroups.length !== 1 ? 's' : ''}
        </span>
      </button>

      {!sectionCollapsed && (
        <div style={{
          border: `1px solid ${dark.border}`,
          borderTop: 'none',
          borderRadius: '0 0 7px 7px',
          backgroundColor: dark.bg,
          padding: '14px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
          gap: 14,
        }}>
          {chartGroups.map(({ def, result }) => (
            <SingleChart
              key={def.id}
              def={def}
              data={result.data}
              bases={result.bases}
              dark={dark}
            />
          ))}
        </div>
      )}
    </div>
  );
}
